import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from '@tanstack/react-router' 
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Loader2, CheckCircle2, Circle, AlertCircle } from "lucide-react";

import { API_ADMIN_BASE_URL } from "@/lib/api-client";

interface SummaryData {
  totalCategories: number;
  totalItems: number;
  overallProgress: number;
  statusGroups: {
    notStarted: number;
    partial: number;
    functional: number;
    almostDone: number;
    complete: number;
    notApplicable?: number;
  };
}

interface CategoryStat {
  id: string;
  title: string;
  todoCount: number;
  averageProgress: number;
  order: number;
}

export const Route = createFileRoute('/dashboard/todo/')({
  component: TodoSummaryPage,
})

function TodoSummaryPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [categories, setCategories] = useState<CategoryStat[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      const [summaryRes, categoriesRes] = await Promise.all([
        fetch(`${API_ADMIN_BASE_URL}/api/admin/todo/summary`, {
          credentials: "include",
        }),
        fetch(`${API_ADMIN_BASE_URL}/api/admin/todo/categories`, {
          credentials: "include",
        }),
      ]);

      const summaryData = await summaryRes.json();
      const categoriesData = await categoriesRes.json();

      if (summaryData.success) setSummary(summaryData.data);
      if (categoriesData.success) setCategories(categoriesData.data);
    } catch (error) {
      console.error("Failed to fetch summary:", error);
    } finally {
      setIsLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Failed to load summary</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Platform Status</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Track implementation progress across all platform features
        </p>
      </div>

      {/* Overall Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overall Progress</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold">{summary.overallProgress}%</div>
            <Progress value={summary.overallProgress} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Total Categories</CardTitle>
            <Circle className="h-3 w-3 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold">{summary.totalCategories}</div>
            <p className="text-xs text-muted-foreground mt-1">Feature groups</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Total Items</CardTitle>
            <AlertCircle className="h-3 w-3 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold">{summary.totalItems}</div>
            <p className="text-xs text-muted-foreground mt-1">Implementation tasks</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Completed</CardTitle>
            <CheckCircle2 className="h-3 w-3 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold">{summary.statusGroups.complete}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.totalItems > 0
                ? Math.round((summary.statusGroups.complete / summary.totalItems) * 100)
                : 0}
              % of total
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Status Distribution */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Status Distribution</CardTitle>
          <CardDescription className="text-xs">Breakdown of implementation status across all features</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {summary.statusGroups.notApplicable !== undefined && summary.statusGroups.notApplicable > 0 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-gray-400" />
                  <span className="text-sm">N/A (Not Applicable)</span>
                </div>
                <span className="text-sm font-medium">{summary.statusGroups.notApplicable}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-red-500" />
                <span className="text-sm">Not Started (0%)</span>
              </div>
              <span className="text-sm font-medium">{summary.statusGroups.notStarted}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-orange-500" />
                <span className="text-sm">Partial (25%)</span>
              </div>
              <span className="text-sm font-medium">{summary.statusGroups.partial}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-yellow-500" />
                <span className="text-sm">Functional (50%)</span>
              </div>
              <span className="text-sm font-medium">{summary.statusGroups.functional}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-blue-500" />
                <span className="text-sm">Almost Done (75%)</span>
              </div>
              <span className="text-sm font-medium">{summary.statusGroups.almostDone}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-green-500" />
                <span className="text-sm">Complete (100%)</span>
              </div>
              <span className="text-sm font-medium">{summary.statusGroups.complete}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Category Progress */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Category Progress</CardTitle>
          <CardDescription className="text-xs">Average completion by feature category</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {categories
              .sort((a, b) => a.order - b.order)
              .map((cat) => (
                <div
                  key={cat.id}
                  className="space-y-2 cursor-pointer hover:bg-muted/50 p-2 rounded-md transition-colors"
                  onClick={() => navigate({ to: `/dashboard/todo/${cat.id}` })}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium hover:text-primary transition-colors">
                      {cat.title}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {cat.averageProgress}% ({cat.todoCount} items)
                    </span>
                  </div>
                  <Progress value={cat.averageProgress} />
                </div>
              ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
